<script setup lang="ts">
import { computed } from 'vue';
import { Check, Folder, ListTree } from 'lucide-vue-next';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenuAction } from '@/components/ui/sidebar';

const props = defineProps<{
	rootPath: string;
}>();

const emit = defineEmits<{
	'change-root': [path: string];
}>();

// split rootPath into ancestor breadcrumb segments
// e.g. "/home/user/Git/ts-ssh2" => ["/", "/home", "/home/user", "/home/user/Git", "/home/user/Git/ts-ssh2"]
const breadcrumbs = computed(() => {
	const path = props.rootPath;
	if (path === '/') return [{ path: '/', name: '/', depth: 0 }];

	const parts = path.split('/').filter(Boolean);
	const result = [{ path: '/', name: '/', depth: 0 }];
	let current = '';
	for (let i = 0; i < parts.length; i++) {
		current += '/' + parts[i]!;
		result.push({ path: current, name: parts[i]!, depth: i + 1 });
	}
	return result;
});

function onSelect(path: string) {
	emit('change-root', path);
}
</script>

<template>
	<DropdownMenu>
		<DropdownMenuTrigger as-child>
			<SidebarMenuAction show-on-hover class="cursor-pointer" title="Change root directory">
				<ListTree />
			</SidebarMenuAction>
		</DropdownMenuTrigger>
		<DropdownMenuContent align="start" side="bottom" :side-offset="4">
			<DropdownMenuItem
				v-for="crumb in breadcrumbs"
				:key="crumb.path"
				class="cursor-pointer"
				:style="{ paddingLeft: `${8 + crumb.depth * 8}px` }"
				@select="onSelect(crumb.path)"
			>
				<Folder class="shrink-0 text-muted-foreground" />
				<span class="truncate">{{ crumb.name }}</span>
				<Check v-if="crumb.path === rootPath" class="ml-auto shrink-0" />
			</DropdownMenuItem>
		</DropdownMenuContent>
	</DropdownMenu>
</template>
